import React, { useState, useEffect } from 'react';
import './PrinterModal.css';

const PrinterModal = ({ isOpen, onClose, cart, breakdown, currencySymbol }) => {
  const [printOption, setPrintOption] = useState('');
  const [isPrinting, setIsPrinting] = useState(false);
  const [printer, setPrinter] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [printers, setPrinters] = useState([]);
  const [loadingPrinters, setLoadingPrinters] = useState(false);
  const [printerConnections, setPrinterConnections] = useState({}); // Track connection status by printer ID

  // Load printers when modal opens
  useEffect(() => {
    if (isOpen) {
      loadPrinters();
    }
  }, [isOpen]);

  const loadPrinters = async () => {
    setLoadingPrinters(true);
    try {
      // Get authentication token from localStorage or session
      const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      if (!token) {
        console.error('No authentication token found');
        return;
      }

      const response = await fetch('/api/printers', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setPrinters(result.data || []);
          // Initialize connection status for all printers as false
          const initialConnections = {};
          result.data.forEach(p => {
            initialConnections[p.id] = false;
          });
          setPrinterConnections(initialConnections);
        }
      } else {
        console.error('Failed to load printers:', response.status);
      }
    } catch (error) {
      console.error('Error loading printers:', error);
    } finally {
      setLoadingPrinters(false);
    }
  };

  const reconnectPrinter = async (printerData) => {
    try {
      // Update connection status to show loading
      setPrinterConnections(prev => ({
        ...prev,
        [printerData.id]: 'connecting'
      }));

      // For now, use the existing Bluetooth connection logic
      // In a real implementation, you'd connect to the specific printer by MAC address or identifier
      const device = await navigator.bluetooth.requestDevice({
        acceptAllDevices: true,
        optionalServices: ['000018f0-0000-1000-8000-00805f9b34fb']
      });

      const server = await device.gatt.connect();
      const service = await server.getPrimaryService('000018f0-0000-1000-8000-00805f9b34fb');
      const characteristic = await service.getCharacteristic('00002af1-0000-1000-8000-00805f9b34fb');

      // Update connection status to connected
      setPrinterConnections(prev => ({
        ...prev,
        [printerData.id]: true
      }));

      // Set as the active printer
      setPrinter({ device, characteristic, printerData });
      setIsConnected(true);

      device.addEventListener('gattserverdisconnected', () => {
        setIsConnected(false);
        setPrinter(null);
        setPrinterConnections(prev => ({
          ...prev,
          [printerData.id]: false
        }));
      });

    } catch (error) {
      console.error('Error reconnecting to printer:', error);
      // Reset connection status on error
      setPrinterConnections(prev => ({
        ...prev,
        [printerData.id]: false
      }));
      alert(`Failed to connect to ${printerData.name}. Make sure Bluetooth is enabled and the printer is in pairing mode.`);
    }
  };

  if (!isOpen) return null;

  // ESC/POS command helper functions
  const ESC = 0x1B;
  const LF = 0x0A;
  const CR = 0x0D;
  const GS = 0x1D;

  const commands = {
    INIT: [ESC, 0x40],
    ALIGN_CENTER: [ESC, 0x61, 0x01],
    ALIGN_LEFT: [ESC, 0x61, 0x00],
    ALIGN_RIGHT: [ESC, 0x61, 0x02],
    BOLD_ON: [ESC, 0x45, 0x01],
    BOLD_OFF: [ESC, 0x45, 0x00],
    DOUBLE_HEIGHT: [GS, 0x21, 0x01],
    NORMAL_SIZE: [GS, 0x21, 0x00],
    CUT_PAPER: [GS, 0x56, 0x00],
    FEED_LINES: (lines) => [ESC, 0x64, lines],
    LINE: '─'.repeat(32)
  };

  const connectPrinter = async () => {
    try {
      setIsPrinting(true);
      
      const device = await navigator.bluetooth.requestDevice({
        acceptAllDevices: true,
        optionalServices: ['000018f0-0000-1000-8000-00805f9b34fb']
      });

      const server = await device.gatt.connect();
      const service = await server.getPrimaryService('000018f0-0000-1000-8000-00805f9b34fb');
      const characteristic = await service.getCharacteristic('00002af1-0000-1000-8000-00805f9b34fb');

      setPrinter({ device, characteristic });
      setIsConnected(true);

      device.addEventListener('gattserverdisconnected', () => {
        setIsConnected(false);
        setPrinter(null);
      });

    } catch (error) {
      console.error('Error connecting to printer:', error);
      alert('Failed to connect to printer. Make sure Bluetooth is enabled and the printer is in pairing mode.');
    } finally {
      setIsPrinting(false);
    }
  };

  const sendToPrinter = async (commandArray) => {
    if (!printer || !isConnected) {
      await connectPrinter();
      if (!printer) return;
    }

    try {
      const uint8Array = new Uint8Array(commandArray);
      await printer.characteristic.writeValue(uint8Array);
    } catch (error) {
      console.error('Print error:', error);
      alert('Failed to print. Please check printer connection.');
    }
  };

  const createKitchenOrder = () => {
    const commands = [];
    
    // Initialize and header
    commands.push(...ESC_COMMANDS.INIT);
    commands.push(...ESC_COMMANDS.ALIGN_CENTER);
    commands.push(...ESC_COMMANDS.BOLD_ON);
    commands.push(...ESC_COMMANDS.DOUBLE_HEIGHT);
    commands.push(...textToBytes('KITCHEN ORDER'));
    commands.push(LF, LF);
    
    commands.push(...ESC_COMMANDS.NORMAL_SIZE);
    commands.push(...ESC_COMMANDS.BOLD_OFF);
    commands.push(...textToBytes(new Date().toLocaleString()));
    commands.push(LF);
    commands.push(...textToBytes(ESC_COMMANDS.LINE));
    commands.push(LF, LF);
    
    // Items
    commands.push(...ESC_COMMANDS.ALIGN_LEFT);
    cart.forEach(item => {
      commands.push(...ESC_COMMANDS.BOLD_ON);
      commands.push(...textToBytes(`${item.name}`));
      commands.push(...ESC_COMMANDS.BOLD_OFF);
      commands.push(LF);
      
      commands.push(...textToBytes(`Qty: ${item.quantity}`));
      commands.push(LF);
      
      if (item.variations && item.variations.length > 0) {
        item.variations.forEach(variation => {
          commands.push(...textToBytes(`  • ${variation.name}`));
          if (variation.price > 0) {
            commands.push(...textToBytes(` (+${currencySymbol}${variation.price.toFixed(2)})`));
          }
          commands.push(LF);
        });
      }
      
      commands.push(...textToBytes(`Order Type: ${item.orderType || 'Dine In'}`));
      commands.push(LF);
      commands.push(...textToBytes('─'.repeat(20)));
      commands.push(LF);
    });
    
    // Footer
    commands.push(LF);
    commands.push(...ESC_COMMANDS.ALIGN_CENTER);
    commands.push(...ESC_COMMANDS.BOLD_ON);
    commands.push(...textToBytes(`Total Items: ${cart.reduce((sum, item) => sum + item.quantity, 0)}`));
    commands.push(LF);
    commands.push(...textToBytes(`Order Total: ${currencySymbol}${breakdown.finalTotal.toFixed(2)}`));
    commands.push(...ESC_COMMANDS.BOLD_OFF);
    commands.push(LF, LF, LF);
    
    // Cut paper
    commands.push(...ESC_COMMANDS.CUT_PAPER);
    
    return commands;
  };

  const createCustomerReceipt = () => {
    const commands = [];
    
    // Initialize and header
    commands.push(...ESC_COMMANDS.INIT);
    commands.push(...ESC_COMMANDS.ALIGN_CENTER);
    commands.push(...ESC_COMMANDS.BOLD_ON);
    commands.push(...textToBytes('CUSTOMER RECEIPT'));
    commands.push(LF);
    commands.push(...ESC_COMMANDS.BOLD_OFF);
    commands.push(...textToBytes('MyPOS System'));
    commands.push(LF);
    commands.push(...textToBytes(new Date().toLocaleString()));
    commands.push(LF);
    commands.push(...textToBytes(ESC_COMMANDS.LINE));
    commands.push(LF);
    
    // Items
    commands.push(...ESC_COMMANDS.ALIGN_LEFT);
    cart.forEach(item => {
      const itemTotal = (parseFloat(item.totalPrice || item.price || 0) * parseInt(item.quantity || 0)).toFixed(2);
      const line = `${item.name}`.padEnd(20) + `${currencySymbol}${itemTotal}`.padStart(12);
      commands.push(...textToBytes(line));
      commands.push(LF);
      
      const qtyLine = `  Qty: ${item.quantity} x ${currencySymbol}${(parseFloat(item.totalPrice || item.price || 0)).toFixed(2)}`;
      commands.push(...textToBytes(qtyLine));
      commands.push(LF);
      
      if (item.variations && item.variations.length > 0) {
        item.variations.forEach(variation => {
          commands.push(...textToBytes(`    • ${variation.name}`));
          if (variation.price > 0) {
            commands.push(...textToBytes(` (+${currencySymbol}${variation.price.toFixed(2)})`));
          }
          commands.push(LF);
        });
      }
      commands.push(LF);
    });
    
    // Breakdown
    commands.push(...textToBytes(ESC_COMMANDS.LINE));
    commands.push(LF);
    commands.push(...textToBytes(`Subtotal:`.padEnd(20) + `${currencySymbol}${breakdown.subtotal.toFixed(2)}`.padStart(12)));
    commands.push(LF);
    commands.push(...textToBytes(`Service (10%):`.padEnd(20) + `${currencySymbol}${breakdown.serviceCharge.toFixed(2)}`.padStart(12)));
    commands.push(LF);
    commands.push(...textToBytes(`SST Tax (6%):`.padEnd(20) + `${currencySymbol}${breakdown.sstTax.toFixed(2)}`.padStart(12)));
    commands.push(LF);
    commands.push(...textToBytes(`Rounding:`.padEnd(20) + `${currencySymbol}${breakdown.roundingAdjustment.toFixed(2)}`.padStart(12)));
    commands.push(LF);
    commands.push(...textToBytes(ESC_COMMANDS.LINE));
    commands.push(LF);
    
    // Total
    commands.push(...ESC_COMMANDS.BOLD_ON);
    commands.push(...textToBytes(`TOTAL:`.padEnd(20) + `${currencySymbol}${breakdown.finalTotal.toFixed(2)}`.padStart(12)));
    commands.push(...ESC_COMMANDS.BOLD_OFF);
    commands.push(LF, LF);
    
    // Footer
    commands.push(...ESC_COMMANDS.ALIGN_CENTER);
    commands.push(...textToBytes('Thank you for your business!'));
    commands.push(LF);
    commands.push(...textToBytes('Visit us again soon'));
    commands.push(LF, LF, LF);
    
    // Cut paper
    commands.push(...ESC_COMMANDS.CUT_PAPER);
    
    return commands;
  };

  const textToBytes = (text) => {
    return Array.from(new TextEncoder().encode(text));
  };

  const ESC_COMMANDS = commands;

  const handlePrint = async (option) => {
    setPrintOption(option);
    setIsPrinting(true);

    try {
      let printCommands;
      
      if (option === 'order-notes') {
        printCommands = createKitchenOrder();
      } else if (option === 'customer-receipt') {
        printCommands = createCustomerReceipt();
      }

      await sendToPrinter(printCommands);
      
    } catch (error) {
      console.error('Print error:', error);
      alert('Failed to print. Please ensure printer is connected.');
    } finally {
      setIsPrinting(false);
      onClose();
    }
  };

  return (
    <div className="printer-modal-overlay" onClick={onClose}>
      <div className="printer-modal-content" onClick={e => e.stopPropagation()}>
        <div className="printer-modal-header">
          <h3>Print Options</h3>
          <button className="close-btn" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="print-options">
          <div className="printer-list-section">
            <h4>Available Printers</h4>
            {loadingPrinters ? (
              <div className="loading-printers">
                <i className="fas fa-spinner fa-spin"></i>
                Loading printers...
              </div>
            ) : printers.length === 0 ? (
              <div className="no-printers">
                <i className="fas fa-print"></i>
                No printers configured
              </div>
            ) : (
              <div className="printer-list">
                {printers
                  .sort((a, b) => (a.department || '').localeCompare(b.department || ''))
                  .map(printer => (
                    <div key={printer.id} className="printer-item">
                      <div className="printer-info">
                        <div className="printer-name">{printer.name}</div>
                        <div className="printer-details">
                          <span className="printer-type">{printer.printer_type}</span>
                          {printer.department && (
                            <span className="printer-department">• {printer.department}</span>
                          )}
                        </div>
                      </div>
                      <div className="printer-status">
                        {printerConnections[printer.id] === true ? (
                          <div className="status-connected" title="Connected">
                            <i className="fas fa-circle"></i>
                          </div>
                        ) : printerConnections[printer.id] === 'connecting' ? (
                          <div className="status-connecting" title="Connecting...">
                            <i className="fas fa-spinner fa-spin"></i>
                          </div>
                        ) : (
                          <button
                            className="status-disconnected"
                            onClick={() => reconnectPrinter(printer)}
                            title="Click to reconnect"
                          >
                            <i className="fas fa-circle"></i>
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </div>

          <button 
            className="print-option-btn kitchen-btn"
            onClick={() => handlePrint('order-notes')}
            disabled={isPrinting}
          >
            <div className="print-icon">
              <i className="fas fa-utensils"></i>
            </div>
            <div className="print-text">
              <span className="option-name">Kitchen Order</span>
              <span className="option-desc">ESC/POS thermal printer format</span>
            </div>
            {isPrinting && printOption === 'order-notes' && (
              <div className="printing-indicator">
                <i className="fas fa-spinner fa-spin"></i>
              </div>
            )}
          </button>

          <button 
            className="print-option-btn receipt-btn"
            onClick={() => handlePrint('customer-receipt')}
            disabled={isPrinting}
          >
            <div className="print-icon">
              <i className="fas fa-receipt"></i>
            </div>
            <div className="print-text">
              <span className="option-name">Customer Receipt</span>
              <span className="option-desc">Detailed thermal receipt with breakdown</span>
            </div>
            {isPrinting && printOption === 'customer-receipt' && (
              <div className="printing-indicator">
                <i className="fas fa-spinner fa-spin"></i>
              </div>
            )}
          </button>
        </div>

        <div className="print-preview">
          <h4>Order Summary</h4>
          <div className="preview-content">
            <div className="preview-items">
              {cart.map((item, index) => (
                <div key={index} className="preview-item">
                  <span className="preview-name">{item.name}</span>
                  <span className="preview-qty">x{item.quantity}</span>
                  <span className="preview-price">
                    {currencySymbol}{((parseFloat(item.totalPrice) || parseFloat(item.price) || 0) * parseInt(item.quantity || 0)).toFixed(2)}
                  </span>
                </div>
              ))}
            </div>
            <div className="preview-total">
              <strong>Total: {currencySymbol}{breakdown.finalTotal.toFixed(2)}</strong>
            </div>
          </div>
        </div>

        <div className="printer-modal-footer">
          <button className="cancel-print-btn" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default PrinterModal;